import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Typography,
  Collapse,
  CircularProgress,
  Stack,
  IconButton,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import {
  Close,
  CheckCircle,
  CompareArrows,
  ThumbDown,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { formatContractDateUTC } from '../utils/dateUtils';
import PaymentBreakdownTable from './PaymentBreakdownTable';
import { DEFAULT_PLATFORM_FEES } from '../utils/feeCalculation';

interface SeasonalRenewalBannerProps {
  onDismiss?: () => void;
}

interface Renewal {
  _id: string;
  currentProviderId?: { businessName?: string; firstName?: string; lastName?: string };
  lastYearServiceType?: string;
  lastYearFrequency?: string;
  serviceCategory?: string;
  renewalFeePercentage?: number;
  currentProviderOffer?: {
    price: number;
    seasonalContractDetails?: {
      contractStartDate?: string;
      contractEndDate?: string;
      totalContractPrice?: number;
      paymentSchedule?: 'single-payment' | 'monthly';
      serviceFrequency?: string;
      monthlyPaymentAmount?: number;
    };
  };
  status: string;
  customerDecision?: string;
  freshBidsRequested?: boolean;
  alternativeBids?: { providerId?: any; price: number }[];
  alternativeProviderIds?: unknown[];
  lowestAlternativeBid?: { price: number };
  expiresAt?: string;
  bannerDismissed?: boolean;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

// Contract dates are stored as UTC midnight (calendar date); format in UTC so Oct 1 shows as Oct 1, not Sep 30

/**
 * Calculate monthly payment amount the same way as the API (subscriptionService.calculateMonthlyPaymentAmount).
 * Uses UTC and start-on-1st vs mid-month rules so the displayed amount matches what the customer will be charged.
 */
function calculateMonthlyPaymentAmountSameAsBackend(
  totalContractPrice: number,
  contractStartDate: Date,
  contractEndDate: Date
): number {
  const startDate = new Date(contractStartDate);
  const endDate = new Date(contractEndDate);
  const startsOnFirst = startDate.getUTCDate() === 1;
  let billingMonths: number;
  if (startsOnFirst) {
    billingMonths = (endDate.getUTCFullYear() - startDate.getUTCFullYear()) * 12 +
      (endDate.getUTCMonth() - startDate.getUTCMonth()) + 1;
  } else {
    // Contract starts mid-month: exclude partial first month; +1 to include end month
    const nextMonthStart = new Date(Date.UTC(
      startDate.getUTCFullYear(),
      startDate.getUTCMonth() + 1,
      1, 0, 0, 0, 0
    ));
    billingMonths = (endDate.getUTCFullYear() - nextMonthStart.getUTCFullYear()) * 12 +
      (endDate.getUTCMonth() - nextMonthStart.getUTCMonth()) + 1;
  }
  if (billingMonths <= 0) return 0;
  return Math.round((totalContractPrice / billingMonths) * 100) / 100;
}

const formatFrequency = (freq?: string): string => {
  if (!freq) return '';
  const f = (freq || '').toLowerCase();
  if (f === 'weekly') return 'Weekly';
  if (f === 'bi-weekly') return 'Bi-Weekly';
  if (f === 'monthly') return 'Monthly';
  if (f === 'per-instance') return 'Per Instance';
  if (f === 'seasonal-contract') return 'Seasonal Contract';
  return freq;
};

const CATEGORY_LABELS: Record<string, string> = {
  snow_removal: 'Snow Removal',
  mowing: 'Mowing',
  fertilizing: 'Weed Control / Fertilizing',
};

const SeasonalRenewalBanner: React.FC<SeasonalRenewalBannerProps> = ({ onDismiss }) => {
  const [renewals, setRenewals] = useState<Renewal[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);

  const fetchOffers = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.get('/seasonal-renewal/customer/offer');
      if (res.success && Array.isArray(res.data)) {
        setRenewals((res.data as Renewal[]) || []);
        setVisible((res.data as Renewal[]).length > 0);
      } else {
        setRenewals([]);
        setVisible(false);
      }
    } catch (e: any) {
      console.error('Failed to fetch renewal offers:', e);
      setRenewals([]);
      setVisible(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOffers();
  }, []);

  const handleAccept = async (renewalId: string) => {
    try {
      setActionLoading(renewalId);
      setError(null);
      setSuccessMessage(null);
      const res = await apiService.post('/seasonal-renewal/customer/accept-current', { renewalId });
      if (res.success) {
        await fetchOffers();
        if (onDismiss) onDismiss();
        setSuccessMessage(res.message || 'Renewal accepted! Your service will continue with the same provider.');
        if ((res as any).needsPaymentSetup) {
          try {
            const paymentRes = await apiService.post<{ success?: boolean; paymentPageUrl?: string }>('/payments/setup-payment-account', {
              ...((res as any).existingCustomerId && { existingCustomerId: (res as any).existingCustomerId }),
            });
            if (paymentRes.success && (paymentRes as any).paymentPageUrl) {
              window.location.href = (paymentRes as any).paymentPageUrl;
              return;
            }
          } catch (_) {
            setSuccessMessage(
              (res.message || 'Renewal accepted!') + ' Please complete payment setup in Settings so we can schedule your service.'
            );
          }
        }
      } else {
        setError(res.error || 'Failed to accept offer');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to accept offer');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = async (renewalId: string) => {
    try {
      setActionLoading(renewalId);
      setError(null);
      const res = await apiService.post('/seasonal-renewal/customer/decline', { renewalId });
      if (res.success) {
        await fetchOffers();
      } else {
        setError(res.error || 'Failed to decline offer');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to decline offer');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSubmitForBids = async (renewalId: string) => {
    try {
      setActionLoading(renewalId);
      setError(null);
      setSuccessMessage(null);
      const res = await apiService.post('/seasonal-renewal/customer/request-fresh-bids', { renewalId });
      if (res.success) {
        await fetchOffers();
        setSuccessMessage(res.message || 'Bid requests sent. New-provider quotes use a 5% platform fee.');
      } else {
        setError(res.error || 'Failed to request bids');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to request bids');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDismiss = async (renewalId?: string) => {
    try {
      await apiService.post('/seasonal-renewal/customer/dismiss', renewalId ? { renewalId } : {});
      await fetchOffers();
      if (!renewalId && onDismiss) onDismiss();
      if (!renewalId) setVisible(false);
    } catch (e: any) {
      console.error('Failed to dismiss renewal banner:', e);
      if (!renewalId) setVisible(false);
    }
  };

  if (loading || !visible || renewals.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        🌱 Seasonal Renewals
      </Typography>
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Stack spacing={2}>
        {renewals.map((r) => {
          const provider = r.currentProviderId;
          const providerName = provider?.businessName || [provider?.firstName, provider?.lastName].filter(Boolean).join(' ') || 'Your provider';
          const serviceLabel = CATEGORY_LABELS[r.serviceCategory || ''] || r.lastYearServiceType || 'Service';
          const price = r.currentProviderOffer?.price ?? 0;
          const isPending = r.status === 'pending' || r.status === 'matched';
          const isDeclined = r.status === 'declined';
          const submitted = isDeclined && r.freshBidsRequested;
          const loadingThis = actionLoading === r._id;

          return (
            <Card key={r._id} variant="outlined" sx={{ position: 'relative' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {serviceLabel} — {providerName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatCurrency(r.currentProviderOffer?.seasonalContractDetails?.totalContractPrice ?? price)}
                      {(() => {
                        const sc = r.currentProviderOffer?.seasonalContractDetails;
                        const isSeasonal = r.lastYearFrequency === 'seasonal-contract';
                        const serviceFreq = isSeasonal && sc?.serviceFrequency
                          ? formatFrequency(sc.serviceFrequency)
                          : r.lastYearFrequency
                            ? formatFrequency(r.lastYearFrequency)
                            : '';
                        return serviceFreq ? ` · ${serviceFreq}` : '';
                      })()}
                      {r.currentProviderOffer?.seasonalContractDetails?.paymentSchedule === 'monthly'
                        ? ' · Monthly payment'
                        : r.currentProviderOffer?.seasonalContractDetails?.paymentSchedule === 'single-payment'
                          ? ' · Single payment'
                          : ''}
                    </Typography>
                    {(() => {
                      const sc = r.currentProviderOffer?.seasonalContractDetails;
                      const start = sc?.contractStartDate;
                      const end = sc?.contractEndDate;
                      if (!start || !end) return null;
                      const startStr = formatContractDateUTC(start);
                      const endStr = formatContractDateUTC(end);
                      if (!startStr || !endStr) return null;
                      return (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          Contract: {startStr} – {endStr}
                        </Typography>
                      );
                    })()}
                    {(() => {
                      const sc = r.currentProviderOffer?.seasonalContractDetails;
                      if (sc?.paymentSchedule !== 'monthly') return null;
                      const total = sc.totalContractPrice ?? r.currentProviderOffer?.price ?? 0;
                      if (total <= 0) return null;
                      let monthlyAmount = sc.monthlyPaymentAmount;
                      if (monthlyAmount == null && sc.contractStartDate && sc.contractEndDate) {
                        monthlyAmount = calculateMonthlyPaymentAmountSameAsBackend(
                          total,
                          new Date(sc.contractStartDate),
                          new Date(sc.contractEndDate)
                        );
                      }
                      if (monthlyAmount == null || monthlyAmount <= 0) return null;
                      return (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          {formatCurrency(monthlyAmount)} per month
                        </Typography>
                      );
                    })()}
                  </Box>
                  <IconButton size="small" onClick={() => handleDismiss(r._id)} aria-label="Dismiss">
                    <Close />
                  </IconButton>
                </Stack>

                {isPending && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <PaymentBreakdownTable
                      compact
                      lines={(() => {
                        const sc = r.currentProviderOffer?.seasonalContractDetails;
                        const feePct = r.renewalFeePercentage ?? DEFAULT_PLATFORM_FEES.renewalFeePercentage;
                        const total = sc?.totalContractPrice ?? price;
                        if (sc?.paymentSchedule === 'monthly' && total > 0 && sc.contractStartDate && sc.contractEndDate) {
                          const monthly =
                            sc.monthlyPaymentAmount ??
                            calculateMonthlyPaymentAmountSameAsBackend(
                              total,
                              new Date(sc.contractStartDate),
                              new Date(sc.contractEndDate)
                            );
                          return [{
                            label: serviceLabel,
                            servicePrice: monthly,
                            platformFeePercent: feePct,
                            chargeNote: 'per month',
                          }];
                        }
                        return [{
                          label: serviceLabel,
                          servicePrice: total,
                          platformFeePercent: feePct,
                          chargeNote: sc?.paymentSchedule === 'single-payment' ? 'at first service' : 'per visit',
                        }];
                      })()}
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        color="success"
                        size="medium"
                        startIcon={loadingThis ? <CircularProgress size={18} color="inherit" /> : <CheckCircle />}
                        disabled={!!actionLoading}
                        onClick={() => handleAccept(r._id)}
                      >
                        Accept
                      </Button>
                      <Button
                        variant="outlined"
                        color="inherit"
                        size="medium"
                        startIcon={<ThumbDown />}
                        disabled={!!actionLoading}
                        onClick={() => handleDecline(r._id)}
                      >
                        Decline
                      </Button>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Declining this renewal will remove this offer. If you request new bids, pricing may be higher than this renewal.
                    </Typography>
                  </>
                )}

                {isDeclined && !submitted && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      This offer is no longer available. You cannot return to it, even if other providers&apos; quotes are higher.
                    </Typography>
                    <Button
                      variant="contained"
                      size="medium"
                      startIcon={loadingThis ? <CircularProgress size={18} color="inherit" /> : <CompareArrows />}
                      disabled={!!actionLoading}
                      onClick={() => handleSubmitForBids(r._id)}
                    >
                      Submit for bids from other providers (5% fee)
                    </Button>
                  </>
                )}

                {isDeclined && submitted && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    {r.alternativeBids && r.alternativeBids.length > 0 ? (
                      <Box>
                        <Typography variant="body2" gutterBottom>
                          {r.alternativeBids.length} provider{r.alternativeBids.length !== 1 ? 's have' : ' has'} submitted bids.
                          {r.lowestAlternativeBid && (
                            <> Lowest: {formatCurrency(r.lowestAlternativeBid.price)}.</>
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          We&apos;ll notify you when all responses are in.
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Bid requests sent to {Array.isArray(r.alternativeProviderIds) ? r.alternativeProviderIds.length : 0} providers. We&apos;ll notify you when they respond.
                      </Typography>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Stack>
      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
        <Button size="small" color="inherit" onClick={() => handleDismiss()}>
          Dismiss all
        </Button>
      </Box>
    </Box>
  );
};

export default SeasonalRenewalBanner;
